import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { selectControls } from '../../redux/controls';
import './Banner.scss';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons';
import AccountModal from '../Modals/AccountModal/AccountModal';
import { useCallback, useEffect, useState } from 'react';
import { LoginModal } from '../Modals/LoginModal/LoginModal';
import { accountLogoutStart, selectAccountLoginLoading, selectAccountLogoutSuccess, selectEmailId, selectIsLoggedIn } from '../../redux/account';
import { LoggedInMenu } from '../LoggedInMenu/LoggedInMenu';
import Modal from '../Modals/Modal';
import ChangePasswordModal from '../Modals/ChangePasswordModal/ChangePasswordModal';
import { Link } from 'react-router-dom';
import { ON_DEV } from '../../shared';
import DeleteAccountModal from '../Modals/DeleteAccountModal/DeleteAccountModal';
import RosterModal from '../Modals/RostersModal/RostersModal';
import Spinner from '../Spinner/Spinner';
import CreateRosterModal from '../Modals/CreateRosterModal/CreateRosterModal';
import { RosterClose, RosterExpandable, RosterRequest } from '../Modals/models';
import EditRosterModal from '../Modals/EditRosterModal/EditRosterModal';

function Banner(): JSX.Element {
  const dispatch = useDispatch();
  // const location = useLocation();
  // const isRoot = location.pathname === '/';
  const controls = useSelector(selectControls);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const emailId = useSelector(selectEmailId);
  const loginLoading = useSelector(selectAccountLoginLoading);
  const logoutSuccess = useSelector(selectAccountLogoutSuccess);

  const [accountIsOpen, setAccountIsOpen] = useState<boolean>(false);
  const [changePasswordIsOpen, setChangePasswordIsOpen] = useState<boolean>(false);
  const [createRosterIsOpen, setCreateRosterIsOpen] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editRosterIsOpen, setEditRosterIsOpen] = useState<boolean>(false);
  const [deleteAccountIsOpen, setDeleteAccountIsOpen] = useState<boolean>(false);
  const [loginIsOpen, setLoginIsOpen] = useState<boolean>(false);
  const [logoutIsOpen, setLogoutIsOpen] = useState<boolean>(false);
  const [rosterIsOpen, setRosterIsOpen] = useState<boolean>(false);
  const [userMenuIsOpen, setUserMenuIsOpen] = useState<boolean>(false);

  const [rosterRequest, setRosterRequest] = useState<RosterRequest>({} as RosterRequest);
  const [rosterToEdit, setRosterToEdit] = useState<RosterExpandable>({} as RosterExpandable);
  const [hasData, setHasData] = useState<boolean>(false);

  //#region Function Callbacks

  const logout = useCallback(() => {
    dispatch(accountLogoutStart());
  }, [dispatch]);



  const rosterOnClose = useCallback((flag: RosterClose): void => {
    if (flag.create) {
      setRosterIsOpen(false);
      setCreateRosterIsOpen(true);
    } else if (flag.edit) {
      setRosterIsOpen(false);
      setRosterToEdit(flag.rosterToEdit ? flag.rosterToEdit : {} as RosterExpandable);
      setEditRosterIsOpen(true);
    } else {
      setRosterIsOpen(false);
      if (hasData) {
        setHasData(false);
        setRosterRequest({} as RosterRequest);
      }
    }
  }, [hasData]);

  const createRosterOnClose = useCallback(({ rosterName, numStudents }: { rosterName?: string, numStudents?: string }): void => {
    if (rosterName && numStudents) {
      setRosterRequest({
        rosterName,
        numStudents
      });
      setHasData(true);
    } else {
      setRosterRequest({} as RosterRequest);
      setHasData(false);
    }
    setRosterIsOpen(true);
    setCreateRosterIsOpen(false);
  }, []);

  const editRosterOnClose = useCallback(({ rosterName, rosterID, numStudents }: RosterRequest) => {
    if (rosterID && rosterName && numStudents) {
      setRosterRequest({
        rosterID,
        rosterName,
        numStudents
      });
      setHasData(true);
    } else {
      setRosterRequest({} as RosterRequest);
      setHasData(false);
    }
    setRosterIsOpen(true);
    setEditRosterIsOpen(false);
  }, []);

  //#endregion Function Callbacks

  useEffect(() => {
    if (logoutSuccess) {
      setLogoutIsOpen(true);
    }
  }, [logoutSuccess]);

  


  // when a modal is open, prevent background scrolling
  useEffect(() => {
    
    if(loginIsOpen || rosterIsOpen || changePasswordIsOpen || deleteAccountIsOpen || accountIsOpen ){
      document.body.classList.add('bg-noscroll');
    } else {
      if(document.body.classList.contains('bg-noscroll')){
        document.body.classList.remove('bg-noscroll');
      }
    }
  }, [loginIsOpen, rosterIsOpen, changePasswordIsOpen, deleteAccountIsOpen, accountIsOpen]);

  

  return (
    <section className="nqt-banner container-fluid" id='banner-app-title' tabIndex={0}>
      <AccountModal open={accountIsOpen} onClose={() => setAccountIsOpen(false)} />
      <ChangePasswordModal open={changePasswordIsOpen} onClose={() => setChangePasswordIsOpen(false)} />
      {
        ON_DEV &&
        <DeleteAccountModal open={deleteAccountIsOpen} onClose={() => setDeleteAccountIsOpen(false)} />
      }
      <LoginModal open={loginIsOpen} onClose={() => setLoginIsOpen(false)} />
      <Modal
        headerText={controls?.LoginHeader?.controlText}
        bodyText={controls?.LoggedOut?.controlText}
        open={logoutIsOpen}
        onClose={() => setLogoutIsOpen(false)}
        windowType="DESC" />
      {
        isLoggedIn &&
        <>
          <RosterModal open={rosterIsOpen} setOpen={setRosterIsOpen} hasData={hasData} rosterRequest={rosterRequest} onClose={rosterOnClose} />
          <CreateRosterModal open={createRosterIsOpen} onClose={createRosterOnClose} />
          <EditRosterModal open={editRosterIsOpen} onClose={editRosterOnClose} rosterToEdit={rosterToEdit} />
        </>
      }
      <div className="row justify-content-between flex-fill">
        <div className="col-4">
          <Link className='nqt-banner--headers nqt-banner--house' id='skip-to-anchor' to={'/'}>
            <h1 className='nqt-banner--dt-header'>{controls && controls?.BlueBarDataTools?.controlText}</h1>
            <h1 className='nqt-banner--qt-header'>{controls && controls?.BlueBarAppTitle?.controlText}</h1>
          </Link>
        </div>
        <div className='col-7 nqt-banner--portals'>
          <div className="row row-cols-2 justify-content-end flex-fill">
            {
              !isLoggedIn ?
                <div className='col flex-fill position-relative'>
                  {
                    loginLoading && <Spinner containerClassName='nqt-banner-spinner' />
                  }
                  <div className="row">
                    <h2 className='nqt-banner--sign-in'>
                      {
                        controls && controls?.BlueBarHaveAcct?.controlText
                      } &nbsp;
                      <button className='nqt-banner--link'
                        disabled={!!loginLoading}
                        tabIndex={0}
                        onClick={() => setLoginIsOpen(true)}>
                        {
                          controls && controls?.BlueBarSignInLink?.controlText
                        }
                      </button>
                    </h2>
                  </div>
                  <div className="row">
                    <h2 className='nqt-banner--create-acc'>
                      {
                        controls && controls?.BlueBarCreateAcct?.controlText
                      } &nbsp;
                      <button className='nqt-banner--link'
                        disabled={!!loginLoading}
                        tabIndex={0}
                        onClick={() => setAccountIsOpen(true)} >
                        {
                          controls && controls?.BlueBarCreateLink?.controlText
                        }
                      </button>
                    </h2>
                  </div>
                </div> :
                <div className='col flex-fill position-relative'>
                  {
                    loginLoading && <Spinner containerClassName='nqt-banner-spinner' />
                  }
                  <div className="row">
                    <h2 className='nqt-banner--signed-in'>
                      {
                        controls && controls?.BlueBarLoggedIn?.controlText
                      }
                      <button className='nqt-banner--link'
                        disabled={!!loginLoading}
                        tabIndex={0}
                        onClick={() => setUserMenuIsOpen(!userMenuIsOpen)}>
                        {
                          emailId
                        }
                      </button>
                      {
                        isLoggedIn &&
                        <LoggedInMenu
                          show={userMenuIsOpen}
                          showCallback={setUserMenuIsOpen}
                          showChangePasswordCallback={setChangePasswordIsOpen}
                          showCreateRosterCallback={setCreateRosterIsOpen}
                          showDeleteAccountCallback={ON_DEV ? setDeleteAccountIsOpen : undefined}
                          showRosterCallback={setRosterIsOpen} />
                      }
                    </h2>
                  </div>
                  <div className="row">
                    <h2 className='nqt-banner--sign-in'>
                      <button className='nqt-banner--link'
                        disabled={!!loginLoading}
                        tabIndex={0}
                        onClick={logout}>
                        {
                          controls && controls?.BlueBarLogOut?.controlText
                        }
                      </button>
                    </h2>
                  </div>
                </div>
            }
            <div className="nqt-login-icon-container col-1">
              <div className="nqt-login-icon-anchor">
                <button className='nqt-login-icon d-flex'
                  id='sign-in-icon'
                  disabled={!!loginLoading}
                  tabIndex={0}
                  onClick={() => !isLoggedIn ? setLoginIsOpen(true) : setUserMenuIsOpen(!userMenuIsOpen)}>
                  <FontAwesomeIcon className='fa-user-circle' icon={faUserCircle} />
                  {
                    !isLoggedIn ?
                      <span className="text-white visually-hidden">{controls?.GotoLogin?.controlText}</span> :
                      <span className="text-white visually-hidden">{controls?.LoginMenu?.controlText}</span>
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Banner;